import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";
import { FileUploader } from "../../../components/fileUploader";
import { TradeMatchingFileType } from "../dataHooks/tradeMatchingQueries";

type Props = {
  open: boolean;
  onClose: () => void;
};

export const UploadDialog: FC<Props> = ({ open, onClose }) => {
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [fileType, setFileType] = useState<TradeMatchingFileType | null>(null);
  const queryClient = useQueryClient();

  const handleUploaded = (message: string) => {
    setUploadMessage(message);
    void queryClient.invalidateQueries({
      queryKey: ["ListTradeMatchingFiles"],
    });
  };

  const handleClose = () => {
    setUploadMessage(null);
    setFileType(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h6" component="span" sx={{ flex: 1 }}>
          Upload trade matching file
        </Typography>
        <IconButton onClick={handleClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 1,
              border: "1px solid",
              borderColor: fileType === null ? "error.main" : "divider",
              bgcolor: (t) =>
                fileType === null
                  ? t.palette.error.light + "14"
                  : "transparent",
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 600 }}
              color={fileType === null ? "error.main" : "text.primary"}
            >
              File type{" "}
              <Box component="span" sx={{ color: "error.main" }}>
                *
              </Box>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5 }}
            >
              Required — choose whether these files are DTCC or REGIS before
              uploading.
            </Typography>
            <ToggleButtonGroup
              value={fileType}
              exclusive
              onChange={(_, next) => {
                if (next !== null) {
                  setFileType(next as TradeMatchingFileType);
                }
              }}
              size="small"
              color="primary"
              sx={{
                "& .MuiToggleButton-root": {
                  px: 2.5,
                  fontWeight: 600,
                  color: "text.primary",
                  "&.Mui-selected": {
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="dtcc">DTCC</ToggleButton>
              <ToggleButton value="regis">REGIS</ToggleButton>
            </ToggleButtonGroup>
            {fileType === null && (
              <Typography
                variant="caption"
                color="error.main"
                sx={{ display: "block", mt: 1, fontWeight: 500 }}
              >
                Please select a file type to enable uploading.
              </Typography>
            )}
          </Box>
          <FileUploader fileType={fileType} onUploaded={handleUploaded} />
          {uploadMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {uploadMessage}
            </Alert>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
